import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { AlertCircle, Loader2, KeyRound, CheckCircle2 } from 'lucide-react';

const RecoverPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';
    const navigate = useNavigate();

    const [checking, setChecking] = useState(true);
    const [validToken, setValidToken] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        const check = async () => {
            if (!token) { setChecking(false); return; }
            try {
                await authAPI.verifyRecovery(token);
                setValidToken(true);
            } catch (err) {
                setError(err.response?.data?.detail || 'Link non valido o scaduto');
            } finally {
                setChecking(false);
            }
        };
        check();
    }, [token]);

    const handleReset = async (e) => {
        e.preventDefault();
        setError('');
        if (newPassword.length < 8) { setError('La nuova password deve avere almeno 8 caratteri'); return; }
        if (newPassword !== confirmPassword) { setError('Le password non coincidono'); return; }
        setLoading(true);
        try {
            await authAPI.resetRecovery(token, newPassword);
            setDone(true);
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore nel reset. Richiedi un nuovo link.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="login-bg min-h-screen flex items-center justify-center p-4"
            style={{ backgroundImage: `linear-gradient(to right, rgba(38, 50, 56, 0.72), rgba(38, 50, 56, 0.45)), url(${process.env.PUBLIC_URL}/login-bg.webp)` }}
        >
            <div className="w-full max-w-md animate-fade-in">
                <Card className="border-0 shadow-2xl">
                    <CardHeader className="space-y-1 pb-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-12 h-12 bg-primary rounded-sm flex items-center justify-center">
                                <span className="text-white font-heading text-xl font-bold">P</span>
                            </div>
                            <div>
                                <CardTitle className="font-heading text-2xl uppercase tracking-tight">Recupero accesso</CardTitle>
                                <CardDescription className="text-xs uppercase tracking-wider">Pezzaioli CRM</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {checking && (
                            <div className="flex items-center justify-center py-8 text-muted-foreground" data-testid="recover-checking">
                                <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Verifica del link...
                            </div>
                        )}

                        {!checking && !validToken && !done && (
                            <div className="space-y-4 text-center" data-testid="recover-invalid">
                                <AlertCircle size={32} className="text-destructive mx-auto" />
                                <p className="text-sm">{error || 'Link non valido o scaduto.'}</p>
                                <Button variant="outline" className="w-full" onClick={() => navigate('/login')} data-testid="recover-goto-login">
                                    Vai al login
                                </Button>
                            </div>
                        )}

                        {!checking && validToken && !done && (
                            <form onSubmit={handleReset} className="space-y-4" data-testid="recover-form">
                                <div className="flex items-center gap-2 text-sm font-medium">
                                    <KeyRound size={18} className="text-primary" /> Imposta una nuova password
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Dopo il salvataggio, al primo accesso ti verrà chiesto di riconfigurare l'app authenticator (MFA).
                                </p>
                                {error && (
                                    <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-sm text-sm" data-testid="recover-error">
                                        <AlertCircle size={16} /><span>{error}</span>
                                    </div>
                                )}
                                <div className="space-y-2">
                                    <Label htmlFor="np" className="text-xs uppercase tracking-wider font-medium">Nuova password</Label>
                                    <Input id="np" type="password" placeholder="Almeno 8 caratteri" value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)} required className="h-11" data-testid="recover-new-password" />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="cp" className="text-xs uppercase tracking-wider font-medium">Conferma password</Label>
                                    <Input id="cp" type="password" placeholder="Ripeti la password" value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)} required className="h-11" data-testid="recover-confirm-password" />
                                </div>
                                <Button type="submit" className="w-full h-11 btn-primary font-heading text-sm" disabled={loading} data-testid="recover-submit">
                                    {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Salvataggio...</>) : 'SALVA NUOVA PASSWORD'}
                                </Button>
                            </form>
                        )}

                        {done && (
                            <div className="space-y-4 text-center" data-testid="recover-done">
                                <CheckCircle2 size={32} className="text-primary mx-auto" />
                                <p className="text-sm">Password reimpostata con successo. Al prossimo accesso dovrai riconfigurare l'MFA.</p>
                                <Button className="w-full btn-primary font-heading" onClick={() => navigate('/login')} data-testid="recover-done-login">
                                    VAI AL LOGIN
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default RecoverPage;
